import { Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { useFormContext } from '@/components/react-hook-form';
import { AcceptRejectSuggestionButtons } from '@/modules/aiSuggestions/components/AcceptRejectSuggestionButtons';
import {
  FormAwareLocateSuggestionButton,
  FormAwareLocateSuggestionButtonVariant,
} from '@/modules/aiSuggestions/components/forms/FormAwareLocateSuggestionButton';
import { SubtrustCreatedFromHelp } from '@/modules/content/tooltipContent/SubtrustCreatedFromHelp';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { COLORS } from '@/styles/tokens/colors';
import { AiSuggestionKind } from '@/types/schema';

import { EditTestamentaryEntityFormShape } from '../EditTestamentaryEntitySplitScreen.types';
import {
  createdFromFieldName,
  useSubtrustCreatedFromSuggestion,
} from '../hooks/useSubtrustCreatedFromSuggestion';
import { useTrustEntityOptions } from '../hooks/useTrustEntityOptions';

export function SubtrustCreatedFromSuggestableInput() {
  const { control } = useFormContext<EditTestamentaryEntityFormShape>();
  const subtrustAISuggestionsEnabled = useFeatureFlag(
    'aikg_v2_associations_poc'
  );
  const { options: trustEntityOptions } = useTrustEntityOptions();
  const {
    createdFromSuggestion,
    createdFromSuggestionAcknowledged,
    createdFromSuggestionDisplay,
    canAcceptCreatedFromSuggestion,
    createdFromSuggestionDisableReason,
    handleAcceptCreatedFromSuggestion,
    handleRejectCreatedFromSuggestion,
  } = useSubtrustCreatedFromSuggestion(trustEntityOptions);

  const showSuggestion =
    subtrustAISuggestionsEnabled &&
    Boolean(createdFromSuggestion) &&
    !createdFromSuggestionAcknowledged;
  const [inputValue, setInputValue] = useState('');

  return (
    <Stack spacing={0}>
      <FormAwareTypeaheadMultiSelectInput<EditTestamentaryEntityFormShape>
        fieldName={createdFromFieldName}
        label="Created from"
        control={control}
        options={trustEntityOptions}
        testId="TestamentaryEntityModal-createdFromEntityIds"
        contextualHelp={<SubtrustCreatedFromHelp />}
        placeholder="Search entities"
        inputValue={inputValue}
        onInputChange={setInputValue}
        labelIconEnd={
          // FIELD variant only renders once the suggestion is Accepted /
          // AutoAccepted, keeping the magnifier visible after the matcher
          // banner disappears.
          subtrustAISuggestionsEnabled && createdFromSuggestion ? (
            <FormAwareLocateSuggestionButton
              suggestion={createdFromSuggestion}
              variant={FormAwareLocateSuggestionButtonVariant.FIELD}
            />
          ) : undefined
        }
      />
      {showSuggestion && createdFromSuggestion && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          spacing={1}
          bgcolor={COLORS.FUNCTIONAL.SUCCESS[50]}
          sx={(theme) => ({
            borderBottomLeftRadius: theme.spacing(0.5),
            borderBottomRightRadius: theme.spacing(0.5),
          })}
          data-testid={`suggestion-${AiSuggestionKind.SubtrustCreatedFrom}`}
        >
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <FormAwareLocateSuggestionButton
              suggestion={createdFromSuggestion}
              variant={FormAwareLocateSuggestionButtonVariant.MATCHER}
            />
            <Typography
              variant="body-xs"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {createdFromSuggestionDisplay || 'Suggested parent trust'}
            </Typography>
          </Stack>
          <AcceptRejectSuggestionButtons
            suggestionKind={AiSuggestionKind.SubtrustCreatedFrom}
            onAcceptSuggestion={handleAcceptCreatedFromSuggestion}
            onRejectSuggestion={handleRejectCreatedFromSuggestion}
            disableAccept={!canAcceptCreatedFromSuggestion}
            acceptDisabledTooltip={createdFromSuggestionDisableReason}
          />
        </Stack>
      )}
    </Stack>
  );
}
